// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { CircularProgress } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import CloseIcon from '@shared/icons/Close';
import { getTestAttr, getDataFromStorageUnit, setDataToStorageUnit } from '@shared/utils/common';
import Ellipsis from '../Ellipsis';
import { Id } from '@shared/types/common';

export interface ExpansionPanelProps {
  className?: string;
  title: string;
  testAttributeArgs: {
    module: string;
    elementDescription: string;
  };
  id: Id;
  rememberState?: boolean;
  removable?: boolean;
  disableToggle?: boolean;
  defaultExpanded?: boolean;
  expanded?: boolean;
  summaryComponent?: React.ReactNode;
  loading?: boolean;
  reference?: React.RefObject<HTMLDivElement>;
  onRemove?: (id: Id) => any;
  onToggle?: (expanded: boolean, id: Id) => any;
  children: React.ReactNode;
}

interface ExpansionPanelState {
  expanded: boolean;
}

const EXPANSION_STORAGE_KEY = 'VI_EXPANSION_PANEL_KEY';

class ExpansionPanel extends React.Component<ExpansionPanelProps, ExpansionPanelState> {
  readonly state: ExpansionPanelState = {
    expanded: Boolean(this.props.defaultExpanded),
  };

  constructor(props) {
    super(props);

    const { rememberState, defaultExpanded, id } = props;

    if (rememberState) {
      const rememberedExpanded = getDataFromStorageUnit(EXPANSION_STORAGE_KEY)[id];
      const expanded = defaultExpanded || rememberedExpanded;

      this.state.expanded = Boolean(expanded);
    }
  }

  private HandleToggle = (e) => {
    const { rememberState, onToggle } = this.props;
    const { id } = e.currentTarget.dataset;
    const nextExpanded = !this.expanded;

    if (rememberState) {
      this.setExpandedStateToStorage(nextExpanded);
    }

    this.setState((state) => ({ ...state, expanded: nextExpanded }));

    if (onToggle) {
      onToggle(nextExpanded, id);
    }
  }

  private onRemove = (e) => {
    const { onRemove } = this.props;
    const { id } = e.currentTarget.dataset;

    if (onRemove) {
      e.stopPropagation();
      onRemove(id);
    }
  }

  setExpandedStateToStorage(nextExpanded: boolean) {
    const { id, defaultExpanded } = this.props;

    if (!defaultExpanded) {
      setDataToStorageUnit(EXPANSION_STORAGE_KEY, id, nextExpanded);
    }
  }

  private get expanded() {
    const { expanded } = this.props;

    return expanded === undefined ? this.state.expanded : expanded;
  }

  render() {
    const {
      id,
      disableToggle,
      title,
      removable,
      children,
      testAttributeArgs,
      summaryComponent,
      loading,
      reference
    } = this.props;
    const classes = withStyles.getClasses(this.props);

    // console.log("****** render ExpansionPanel:", children);
    return (
      <Accordion
        className={classes.root}
        {...getTestAttr(testAttributeArgs.module, `${testAttributeArgs.elementDescription}`, 'expansion-panel-field')}
        expanded={this.expanded}
        onChange={disableToggle ? undefined : this.HandleToggle}
        ref={reference}
      >
        <AccordionSummary
          classes={{
            root: classes.summaryRoot,
            expanded: classes.summaryExpanded,
            expandIconWrapper: classes.expandIcon,
            content: classes.summaryContent
          }}
          expandIcon={<ExpandMoreIcon />}
          data-id={id}
        >
          <Ellipsis maxWidth={400} classes={{ root: classes.summaryText }} text={title} />
          {loading && <CircularProgress classes={{ root: classes.loading }} size={15} />}
          {summaryComponent}
          {removable && this.expanded && <CloseIcon className={classes.removeIcon} data-id={id} onClick={this.onRemove} />}
        </AccordionSummary>
        <AccordionDetails
          classes={{
            root: classes.detailsRoot
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    );
  }
}

const ExpansionPanelStyled = withStyles(
  ExpansionPanel, 
  (theme, props) => ({
    "root": {},
    "summaryRoot": {},
    "expandIcon": {
      position: 'absolute',
      left: 0,
      padding: 0,
      zIndex: 2,
    },
    "summaryContent": {
      position: 'relative',
    },
    "summaryExpanded": {},
    "summaryText": {
      fontWeight: 500,
      fontSize: 19,
      marginRight: theme.spacing(4),
    },
    "removeIcon": {
      position: 'absolute',
      right: 0,
    },
    "detailsRoot": {},
    "loading": {
      marginRight: theme.spacing(4),
    }
  })
);

export default ExpansionPanelStyled;